/** Local single-player session: red player vs 5 green targets. */

import { SIM_HZ, BATTLEFIELD_SIZE, DEFAULT_CONFIG } from '../shared/config.js';
import { Simulation, type SimEvents } from '../shared/physics.js';
import type { GameSnapshot } from '../shared/protocol.js';

const TARGET_COUNT = 5;
const SP_SLOTS = 1 + TARGET_COUNT;

export type SpMissileTrail = {
  id: string;
  slot: number;
  trail: { x: number; y: number }[];
  live: boolean;
  isDebris: boolean;
};

export type SpUpdate = (
  snapshot: GameSnapshot,
  events: SimEvents,
  missileTrails: SpMissileTrail[],
) => void;

export class SinglePlayerSession {
  private sim: Simulation;
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  onUpdate: SpUpdate | null = null;

  constructor() {
    this.sim = new Simulation(SP_SLOTS, Date.now() ^ (Math.random() * 1e9), DEFAULT_CONFIG.numPlanets);
    this.sim.activatePlayer(0, 'sp-you', 'You', true);
    for (let i = 1; i <= TARGET_COUNT; i++) {
      this.sim.activatePlayer(i, `sp-t${i}`, '', true);
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.emit(this.sim.step());
    this.timer = setInterval(() => {
      if (!this.running) return;
      this.emit(this.sim.step());
    }, 1000 / SIM_HZ);
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  setAngle(units: number): void {
    this.sim.setAngleUnits(0, units);
  }

  setVelocity(v: number): void {
    this.sim.setVelocity(0, v);
  }

  fire(): void {
    this.sim.requestFire(0);
  }

  private collectMissileTrails(): SpMissileTrail[] {
    const out: SpMissileTrail[] = [];
    for (const p of this.sim.players) {
      for (const m of p.shots) {
        if (!m) continue;
        out.push({
          id: m.id,
          slot: m.ownerSlot,
          trail: m.trail,
          live: m.live,
          isDebris: m.isDebris,
        });
      }
    }
    return out;
  }

  private emit(events: SimEvents): void {
    this.onUpdate?.(this.buildSnapshot(), events, this.collectMissileTrails());
  }

  private buildSnapshot(): GameSnapshot {
    const sim = this.sim;
    const players = sim.players
      .filter((p) => p.active)
      .map((p) => ({
        id: p.playerId,
        slot: p.slot,
        name: p.name,
        x: p.position.x,
        y: p.position.y,
        energy: p.energy,
        points: p.points,
        connected: true,
        colorSlot: p.slot,
      }));

    return {
      code: 'SOLO',
      phase: sim.mode === 'board' ? 'board' : 'playing',
      tick: sim.tick,
      simTime: sim.tick / SIM_HZ,
      timeRemainSec: sim.timeRemainSec(),
      battlefieldSize: BATTLEFIELD_SIZE,
      planets: sim.planets.map((pl) => ({
        x: pl.position.x,
        y: pl.position.y,
        radius: pl.radius,
      })),
      players,
      missiles: sim.liveMissiles().map((m) => ({
        id: m.id,
        ownerId: sim.players[m.ownerSlot]?.playerId ?? '',
        slot: m.ownerSlot,
        x: m.position.x,
        y: m.position.y,
        live: m.live,
        ageTicks: m.ageTicks,
        isDebris: m.isDebris,
      })),
      killFlash: sim.killFlash,
      maxPlayers: SP_SLOTS,
      isPublic: false,
      creatorId: 'sp-you',
    };
  }
}
